import { isArray } from "../../../utils";
import type { FieldNamesProps } from "@/components/proTable/interface";
import { getValueByProp } from "./object";
import { CDN_URL, DEFAULT_VALUE } from "@/utils/config";
import Tenant from "@/function/tenant";
import { getSupplyChainName } from "@/function/roleCode";

/**
 * @description 获取localStorage
 * @param {String} key Storage名称
 * @returns {String}
 */
export function localGet(key: string) {
    const value = window.localStorage.getItem(`zhihao-page${key}`);
    try {
        return JSON.parse(window.localStorage.getItem(`zhihao-page${key}`) as string);
    } catch {
        return value;
    }
}

/**
 * @description 存储localStorage
 * @param {String} key Storage名称
 * @param {*} value Storage值
 * @returns {void}
 */
export function localSet(key: string, value: any) {
    window.localStorage.setItem(`zhihao-page${key}`, JSON.stringify(value));
}

/**
 * @description 清除localStorage
 * @param {String} key Storage名称
 * @returns {void}
 */
export function localRemove(key: string) {
    window.localStorage.removeItem(`zhihao-page${key}`);
}

/**
 * @description 清除所有localStorage
 * @returns {void}
 */
export function localClear() {
    window.localStorage.clear();
}

/**
 * @description 清除cookie 中的某个字段
 * @returns {void}
 */
export function deleteCookie(key: string): void {
    // 检查当前环境是否支持 document.cookie
    if (typeof document === "undefined" || !document.cookie) {
        return;
    }
    document.cookie = `${key}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/;`;
}

/**
 * @description 判断数据类型
 * @param {*} val 需要判断类型的数据
 * @returns {String}
 */
export function isType(val: any) {
    if (val === null) return "null";
    if (typeof val !== "object") return typeof val;
    else return Object.prototype.toString.call(val).slice(8, -1).toLocaleLowerCase();
}

/**
 * @description 生成唯一 uuid
 * @returns {String}
 */
export function generateUUID() {
    let uuid = "";
    for (let i = 0; i < 32; i++) {
        const random = (Math.random() * 16) | 0;
        if (i === 8 || i === 12 || i === 16 || i === 20) uuid += "-";
        uuid += (i === 12 ? 4 : i === 16 ? (random & 3) | 8 : random).toString(16);
    }
    return uuid;
}

/**
 * 判断两个对象是否相同
 * @param {Object} a 要比较的对象一
 * @param {Object} b 要比较的对象二
 * @returns {Boolean} 相同返回 true，反之 false
 */
export function isObjectValueEqual(a: { [key: string]: any }, b: { [key: string]: any }) {
    if (!a || !b) return false;
    const aProps = Object.getOwnPropertyNames(a);
    const bProps = Object.getOwnPropertyNames(b);
    if (aProps.length != bProps.length) return false;
    for (let i = 0; i < aProps.length; i++) {
        const propName = aProps[i];
        const propA = a[propName];
        const propB = b[propName];
        if (!b.hasOwnProperty(propName)) return false;
        if (propA instanceof Object) {
            if (!isObjectValueEqual(propA, propB)) return false;
        } else if (propA !== propB) {
            return false;
        }
    }
    return true;
}

/**
 * @description 生成随机数
 * @param {Number} min 最小值
 * @param {Number} max 最大值
 * @returns {Number}
 */
export function randomNum(min: number, max: number): number {
    const num = Math.floor(Math.random() * (min - max) + max);
    return num;
}

/**
 * @description 获取当前时间对应的提示语
 * @returns {String}
 */
export function getTimeState() {
    const timeNow = new Date();
    const hours = timeNow.getHours();
    if (hours >= 6 && hours <= 10) return `早上好 ⛅`;
    if (hours >= 10 && hours <= 14) return `中午好 🌞`;
    if (hours >= 14 && hours <= 18) return `下午好 🌞`;
    if (hours >= 18 && hours <= 24) return `晚上好 🌛`;
    if (hours >= 0 && hours <= 6) return `凌晨好 🌛`;
}

/**
 * @description 格式化表格单元格默认值 (el-table-column)
 * @param {Number} row 行
 * @param {Number} col 列
 * @param {*} callValue 当前单元格值
 * @returns {String}
 * */
export function formatTableColumn(row: number, col: number, callValue: any) {
    // 如果当前值为数组，使用 / 拼接（根据需求自定义）
    if (isArray(callValue)) return callValue.length ? callValue.join(" / ") : DEFAULT_VALUE;
    return callValue ?? DEFAULT_VALUE;
}

/**
 * @description 处理 ProTable 值为数组 || 无数据
 * @param {*} callValue 需要处理的值
 * @returns {String}
 * */
export function formatValue(callValue: any) {
    // 如果当前值为数组，使用 / 拼接（根据需求自定义）
    if (isArray(callValue)) return callValue.length ? callValue.join(" / ") : DEFAULT_VALUE;
    return callValue ?? DEFAULT_VALUE;
}

/**
 * @description 处理 prop 为多级嵌套的情况，返回的数据 (列如: prop: user.name)
 * @param {Object} row 当前行数据
 * @param {String} prop 当前 prop
 * @returns {*}
 * */
export function handleRowAccordingToProp(row: { [key: string]: any }, prop: string) {
    if (!prop.includes(".")) return row[prop] ?? DEFAULT_VALUE;
    prop.split(".").forEach(item => (row = row[item] ?? DEFAULT_VALUE));
    return row;
}

/**
 * @description 处理 prop，当 prop 为多级嵌套时 ==> 返回最后一级 prop
 * @param {String} prop 当前 prop
 * @returns {String}
 * */
export function handleProp(prop: string) {
    const propArr = prop.split(".");
    if (propArr.length == 1) return prop;
    return propArr[propArr.length - 1];
}

/**
 * @description 根据枚举列表查询当需要的数据（如果指定了 label 和 value 的 key值，会自动识别格式化）
 * @param {String} callValue 当前单元格值
 * @param {Array} enumData 字典列表
 * @param {Array} fieldNames label && value && children 的 key 值
 * @param {String} type 过滤类型（目前只有 tag）
 * @returns {String}
 * */
export function filterEnum(callValue: any, enumData?: any, fieldNames?: FieldNamesProps, type?: "tag") {
    const value = fieldNames?.value ?? "value";
    const label = fieldNames?.label ?? "label";
    const children = fieldNames?.children ?? "children";
    let filterData: { [key: string]: any } = {};
    // 判断 enumData 是否为数组
    if (Array.isArray(enumData)) filterData = findItemNested(enumData, callValue, value, children);
    // 判断是否输出的结果为 tag 类型
    if (type == "tag") {
        return filterData?.tagType ? filterData.tagType : "";
    } else {
        return filterData ? filterData[label] : DEFAULT_VALUE;
    }
}

/**
 * @description 根据枚举列表查询当需要的数据对象（如果指定了 label 和 value 的 key值，会自动识别格式化）
 * @param {String} callValue 当前单元格值
 * @param {Array} enumData 字典列表
 * @param {Array} fieldNames label && value && children 的 key 值
 * @param {String} type 过滤类型（目前只有 tag）
 * @returns {String}
 * */
export function filterEnumObj(callValue: any, enumData?: any, fieldNames?: FieldNamesProps) {
    const value = fieldNames?.value ?? "value";
    const children = fieldNames?.children ?? "children";
    let filterData: { [key: string]: any } = {};
    // 判断 enumData 是否为数组
    if (Array.isArray(enumData)) filterData = findItemNested(enumData, callValue, value, children);
    return filterData;
}

/**
 * @description 递归查找 callValue 对应的 enum 值
 * */
export function findItemNested(enumData: any, callValue: any, value: string, children: string) {
    return enumData.reduce((accumulator: any, current: any) => {
        if (accumulator) return accumulator;
        if (current[value] === callValue) return current;
        if (current[children]) return findItemNested(current[children], callValue, value, children);
    }, null);
}

export const $get = getValueByProp;

/** 空串转undefined */
export const empty2Undefined = <T extends Record<string, any>>(obj: T): T => {
    Object.keys(obj).forEach((key: keyof T) => {
        if (obj[key] === "") obj[key] = undefined as any;
    });
    return obj;
};

/** 拼接文件路径 */
export const getBaseFileUrl = (path: string) => {
    const baseFileUrl = import.meta.env.VITE_BASE_FILE_URL;

    return baseFileUrl + path;
};

/** 获取logoUrl */
export const getLogoUrl = () => {
    return Tenant.getInstance().theme.loginLogo;
};

/** 拼接文件路径 */
export const getPublicFileUrl = (path: string) => {
    return CDN_URL + "customer-admin/" + path;
};
/**拼接css背景图片 */
export const getPublicCssUrl = (path: string) => {
    return `url(${getPublicFileUrl(path)})`;
};
/**双值组合 */
export const valueCombine = (a?: string, b?: string, sep = "-") => {
    if (!a && !b) return DEFAULT_VALUE;
    if (!a) return b;
    if (!b) return a;
    return a + sep + b;
};

/** 合同相关获取真实state */
// 获取真实的state
export const getRealyState = (state: number | string): number => {
    // 先获取高位值
    const code = Number(state) >> 16;
    return code;
};
/**
 *
 * @param type 1 供应商-采购商 2联合购销商-采购商 3 供应商-联合购销商
 * @param upDown 0 采购商  1 供应商、联合购销商
 */
export const getIdentity = (type: number = 0, upDown: number) => {
    if (!type) return DEFAULT_VALUE;
    if (type == 1) {
        return upDown ? "供应商" : "采购商";
    }
    if (type == 2) {
        return upDown ? getSupplyChainName() : "采购商";
    }
    if (type == 3) {
        return upDown ? "供应商" : getSupplyChainName();
    }
};
